import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        byCourse: {},
    };
}
const moduleBuilder = storeBuilder.module('enroll-links', makeInitialState());
/** The store for the enrollLinks. */
export var EnrollLinksStore;
(function (EnrollLinksStore) {
    /** Resets the enroll links state to its initial values. */
    EnrollLinksStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    const commitEnrollLinks = moduleBuilder.commit((state, { courseId, data, }) => {
        utils.vueSet(state.byCourse, courseId, data);
    }, 'commitEnrollLinks');
    /** Updates the list of enroll links for a course using a provided updater function. */
    const commitUpdateCourseEnrollLinks = moduleBuilder.commit((state, { courseId, updater, }) => {
        const existing = state.byCourse[courseId];
        if (existing == null) {
            return;
        }
        commitEnrollLinks({
            courseId,
            data: {
                ...existing,
                data: existing.data?.map(g => updater(g)),
            },
        });
    }, 'commitEnrollLink');
    /** Gets the paginated list of enroll links for a given course. */
    EnrollLinksStore.getForCourse = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param courseId The ID of the course.
     * @returns A paginated state object containing the enroll links.
     */
    (courseId) => utils.getPaginatedState(state.byCourse[courseId]), 'getForCourse');
    /** Fetches and stores the paginated list of enroll links for a course. */
    EnrollLinksStore.loadForCourse = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { courseId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitEnrollLinks({ courseId, data }),
            getData: () => state.byCourse[courseId],
            load: async (nextToken) => {
                const res = await client.p.course.getAllEnrollLinks({
                    courseId,
                    nextToken,
                });
                return res;
            },
            toLoad,
        });
    }, 'loadForCourse');
    /** Creates a new enroll link for a course and adds it to the local state on success. */
    EnrollLinksStore.createEnrollLink = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves with the API response.
     */
    async ({ state }, { courseId, data, }) => {
        const res = await client.p.course.putEnrollLink({
            courseId,
            requestBody: data,
        });
        return res.addCallbacks({
            onSuccess: () => {
                const paginatedState = state.byCourse[courseId] ?? {
                    pagination: { tag: 'not-started' },
                };
                commitEnrollLinks({
                    courseId,
                    data: {
                        ...paginatedState,
                        data: [res.cgResult, ...(paginatedState.data ?? [])],
                    },
                });
            },
        });
    }, 'createEnrollLink');
    /** Updates an existing enroll link and updates it in the local state on success. */
    EnrollLinksStore.patchEnrollLink = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param _ The module context (unused).
     * @param root1 The action payload.
     * @returns A promise that resolves with the API response.
     */
    async (_, { courseId, data, }) => {
        const res = await client.p.course.putEnrollLink({
            courseId,
            requestBody: data,
        });
        return res.addCallbacks({
            onSuccess: () => {
                commitUpdateCourseEnrollLinks({
                    courseId,
                    updater: old => (old.id === data.id ? res.cgResult : old),
                });
            },
        });
    }, 'patchEnrollLink');
    /** Deletes an enroll link from the server and removes it from the local state. */
    EnrollLinksStore.deleteEnrollLink = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves with the API response.
     */
    async ({ state }, { courseId, linkId, }) => {
        const res = await client.p.course.deleteEnrollLink({ courseId, linkId });
        return res.addCallbacks({
            onAfterSuccess: () => {
                const data = state.byCourse[courseId];
                if (data == null) {
                    return;
                }
                commitEnrollLinks({
                    courseId,
                    data: {
                        ...data,
                        data: data.data?.filter(gs => gs.id !== linkId),
                    },
                });
            },
        });
    }, 'deleteEnrollLink');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        EnrollLinksStore.commitClearAll();
    });
})(EnrollLinksStore || (EnrollLinksStore = {}));
